import {
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import type { Card, ScoreBreakdown, TallyStep } from '../../engine';
import type { AiSpeed } from '../../app/settings';
import { sounds } from '../../app/sound';
import { t } from '../../i18n';
import { patternName } from '../../app/describe';
import { flare as flareScene, patchScene } from '../scene';
import { CardView } from './CardView';
import { Emblem } from './night/Emblem';
import { NightObject } from './night/Objects';
import { SUPERSTITION_INFO } from '../../campaign';
import { itemName } from '../../app/describe';
import {
  SCORE_PACE,
  ScoreNumber,
  formatM,
  formatScore,
  scoreBeats,
  type ScoreBeat,
} from './ScoreBurst';

/**
 * The Tally (docs/LONG_NIGHT_ART_DIRECTION.md §11.7, §9.2–§9.3): a Long Night chain counted the
 * way Balatro counts a hand, the one place where the run plays at full volume. Points in a blue
 * box times the multiplier in a red one; every beat a little quicker and a step higher on the
 * scale than the last; each Superstition that adds something wiggles on the Mantel and sends its
 * chip flying into its box; the pattern is named in ink; Full Circle brushes an ensō round the
 * pawn. Then a hush and the slam, harder the bigger the score; candle flame when the chain wins
 * the round on its own; "Best chain" written under the run's record. The Broker's count is
 * written, not slammed: pen strokes, no sparkles, a quiet underline. A tap skips to the end.
 *
 * It moves only by transform and opacity, and never changes size while it counts: every row it
 * will need is there from the start (its boxes as wide as their widest number), so nothing under
 * the eye jumps when a line, an item or the pattern comes in. What leaves (an item, a line) goes
 * smoothly while the next comes.
 */

/** Sparkles by tier (§9.3): left %, top %, delay ms. */
const SPARKS: readonly (readonly [number, number, number])[] = [
  [2, 18, 0],
  [88, 8, 90],
  [12, 70, 160],
  [80, 66, 60],
  [46, -4, 220],
  [60, 84, 300],
  [30, -8, 120],
  [96, 44, 200],
  [-4, 46, 260],
  [22, 96, 340],
  [70, -10, 40],
  [104, 80, 380],
];

/** How hard a score lands (§9.3). */
export const tierOf = (score: number): 0 | 1 | 2 => (score >= 10000 ? 2 : score >= 100 ? 1 : 0);

/** Each beat a little quicker than the last (×0.92), never under 90 ms; the whole within ~6 s. */
function beatTimes(n: number, pace: AiSpeed): number[] {
  const first = SCORE_PACE[pace].beat;
  const cap = pace === 'fast' ? 2400 : pace === 'slow' ? 8000 : 6000;
  const out: number[] = [];
  let d = first;
  for (let i = 0; i < n; i++) {
    out.push(Math.max(90, d));
    d *= 0.92;
  }
  const total = out.reduce((a, b) => a + b, 0);
  return total > cap ? out.map((x) => Math.max(60, (x * cap) / total)) : out;
}

/**
 * An item's beat (§11.7) is slower than the others, so it is seen: it flies up from where it sits,
 * lands over the count and jiggles, holds a moment, then sends its chip down into the box; the next
 * beat waits for the chip to land. In ms, before a long chain's items speed up.
 */
const ITEM_BEAT: Record<AiSpeed, { readonly fly: number; readonly hold: number }> = {
  fast: { fly: 220, hold: 160 },
  normal: { fly: 340, hold: 320 },
  slow: { fly: 420, hold: 460 },
};
/** A chip's flight into its box (styles.css, chip-fly), and a breath once it lands. */
const CHIP_MS = 440;
const AFTER_CHIP_MS = 80;

/** One beat's timing (ms from its start): the item lands, its chip leaves, the box counts it. */
interface BeatPlan {
  readonly d: number;
  readonly item: boolean;
  readonly fly: number;
  readonly launch: number;
  readonly hit: number;
}

function beatPlan(
  beats: readonly { readonly item: boolean; readonly chip: boolean }[],
  pace: AiSpeed,
  still: boolean,
): BeatPlan[] {
  const base = beatTimes(beats.length, pace);
  const { fly, hold } = ITEM_BEAT[pace];
  let k = 0;
  return beats.map((b, i) => {
    const d = base[i]!;
    if (!b.item) return { d, item: false, fly: 0, launch: 0, hit: 0 };
    // Many items in one chain: each a little quicker than the last, never under two thirds.
    const f = Math.max(0.66, 0.92 ** k++);
    const up = still ? 0 : Math.round(fly * f);
    const launch = still ? Math.round((fly + hold) * f) : up + Math.round(hold * f);
    const hit = launch + (b.chip ? CHIP_MS : 0);
    return { d: Math.max(d, hit + AFTER_CHIP_MS), item: true, fly: up, launch, hit };
  });
}

/** The element a step comes from, if the screen shows it: a Mantel niche, a pawn, a placard. */
function sourceOf(step: TallyStep | undefined, pawnId: string): Element | null {
  if (!step) return null;
  const q = (sel: string) => document.querySelector(sel);
  switch (step.source) {
    case 'superstition':
      return step.uid ? q(`[data-tally-uid="${CSS.escape(step.uid)}"]`) : null;
    case 'carving':
    case 'status':
    case 'landing':
    case 'capture':
    case 'crown':
      return q(`[data-pawn="${CSS.escape(pawnId)}"]`);
    case 'omen':
      return step.id ? q(`.sq-omen.omen-${CSS.escape(step.id)}`) : null;
    case 'houseRule':
      return step.id ? q(`[data-tally-rule="${CSS.escape(step.id)}"]`) : null;
    default:
      return null;
  }
}

/**
 * The thing a step comes from, drawn large in the count while it fires (as Balatro's jokers jump
 * when they count): a Superstition in its frame, a Carving or an Omen as its emblem. Steps from
 * the move itself (landings, captures, the pattern) have none: the board shows those.
 */
function actorOf(step: TallyStep | undefined, size: number) {
  if (!step?.id) return null;
  switch (step.source) {
    case 'superstition': {
      const rarity = SUPERSTITION_INFO[step.id as keyof typeof SUPERSTITION_INFO]?.rarity;
      return (
        <NightObject
          spec={{ kind: 'superstition', emblem: step.id, ...(rarity ? { rarity } : {}) }}
          size={size}
        />
      );
    }
    case 'carving':
      return <Emblem name={`carving.${step.id}`} size={size * 0.8} />;
    case 'omen':
      return <Emblem name={`omen.${step.id}`} size={size * 0.8} />;
    default:
      return null;
  }
}

/** Its name, for the line under it: "+4 Mult · Red Thread". */
function actorName(step: TallyStep | undefined): string | null {
  if (!step?.id) return null;
  switch (step.source) {
    case 'superstition':
      return itemName(step.id);
    case 'carving':
      return itemName(step.id);
    case 'omen':
      return itemName(`omen.${step.id}`);
    default:
      return null;
  }
}

interface Chip {
  readonly key: number;
  readonly text: string;
  readonly box: ScoreBeat['box'];
  readonly heavy: boolean;
  readonly x: number;
  readonly y: number;
  readonly dx: number;
  readonly dy: number;
}

interface Enso {
  readonly key: number;
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

type Phase = 'count' | 'hush' | 'slam' | 'roll' | 'leaving';

export function TallyView({
  breakdown,
  who,
  source,
  pawnId,
  half,
  pace,
  reduceMotion,
  ink,
  tint,
  flare,
  record,
  level,
  cards,
  onLanded,
  onDone,
}: {
  breakdown: ScoreBreakdown;
  who: string;
  source: 'chain' | 'epitaph';
  /** The chain's pawn: an ensō round it at Full Circle; its Carvings and statuses fire on it. */
  pawnId: string;
  /** Over the half of the board of the side scoring, near that side's Mantel. */
  half: 'top' | 'bottom';
  pace: AiSpeed;
  reduceMotion: boolean;
  /** The Broker's count, written in ink. */
  ink: boolean;
  /** A regular's color, for their sparkles. */
  tint?: string;
  /** The chain wins the round on its own: candle flame. */
  flare: boolean;
  /** The best chain of the run so far (from Night II). */
  record: boolean;
  /** The Appraisal level of the chain's pattern, shown beside its name. */
  level: number;
  /** The cards of the table: a Stamp or a Finish fires on its card. */
  cards: Readonly<Record<string, Card>>;
  onLanded: () => void;
  onDone: () => void;
}) {
  const steps = useMemo(() => (breakdown.steps ?? []).filter((s) => !s.pending), [breakdown.steps]);
  const beats = useMemo(() => scoreBeats(breakdown), [breakdown]);
  /** Beats under way (the item and the line shown), and beats counted into the boxes. */
  const [counted, setCounted] = useState(0);
  const [hit, setHit] = useState(0);
  const [phase, setPhase] = useState<Phase>('count');
  const [chips, setChips] = useState<Chip[]>([]);
  const [enso, setEnso] = useState<Enso | null>(null);
  const boxP = useRef<HTMLSpanElement>(null);
  const boxM = useRef<HTMLSpanElement>(null);
  const beatAt = useRef<HTMLSpanElement>(null);
  const actorAt = useRef<HTMLSpanElement>(null);
  const landed = useEffectEvent(() => onLanded());
  const done = useEffectEvent(() => onDone());
  const score = breakdown.score;
  const tier = tierOf(score);
  const inkOut = score >= 1e100;

  const plan = useMemo(
    () =>
      beatPlan(
        beats.map((b, i) => ({
          item: !!actorOf(steps[i], 1),
          chip: !reduceMotion && !ink && b.box !== 'brass',
        })),
        pace,
        reduceMotion,
      ),
    [beats, steps, pace, reduceMotion, ink],
  );

  /** A beat begins: its source fires where it sits (and an item starts up toward the count). */
  const fire = useEffectEvent((i: number) => {
    const from = sourceOf(steps[i], pawnId);
    if (!(from instanceof HTMLElement || from instanceof SVGElement)) return;
    // Two copies of the wiggle take turns (styles.css, data-fire), so that an element firing
    // again before the last one ends starts it over, with no layout forced to do it.
    const turn = from.dataset.fire === 'a' ? 'b' : 'a';
    from.dataset.fire = turn;
    from.classList.add(reduceMotion ? 'is-firing-still' : 'is-firing');
    setTimeout(() => {
      if (from.dataset.fire !== turn) return;
      from.classList.remove('is-firing', 'is-firing-still');
      delete from.dataset.fire;
    }, 340);
  });

  /** A beat's chip leaves for its box: from the item over the count, else from the table. */
  const launch = useEffectEvent((i: number) => {
    const beat = beats[i];
    const step = steps[i];
    if (!beat || reduceMotion || ink || beat.box === 'brass') return;
    const kind = step?.op === 'x' ? 'x' : beat.box;
    const from = sourceOf(step, pawnId);
    // The item gives a little as its chip leaves it.
    if (plan[i]?.item) actorAt.current?.classList.add('is-sending');
    // Drawn on this beat's render, so measured on the next frame.
    requestAnimationFrame(() => {
      const target = (beat.box === 'p' ? boxP : boxM).current;
      const origin = actorAt.current ?? from ?? beatAt.current;
      if (!target || !origin) return;
      const a = origin.getBoundingClientRect();
      const b = target.getBoundingClientRect();
      const x = a.left + a.width / 2;
      // From the item's lower edge, so the chip never hides it; from the top of a niche or pawn.
      const y =
        origin === actorAt.current ? a.bottom - 6 : origin === from ? a.top : a.top + a.height / 2;
      const chip: Chip = {
        key: performance.now() + i,
        text: beat.text,
        box: beat.box,
        heavy: kind === 'x',
        x,
        y,
        dx: b.left + b.width / 2 - x,
        dy: b.top + b.height / 2 - y,
      };
      setChips((c) => [...c.slice(-10), chip]);
    });
  });

  /** A beat counted into its box: its note, a step higher than the last; Full Circle's ensō. */
  const count = useEffectEvent((i: number) => {
    const beat = beats[i];
    const step = steps[i];
    if (!beat) return;
    sounds.tallyBeat(i, step?.op === 'x' ? 'x' : beat.box, ink);
    // The box takes the hit: it swells and flashes, every time (a ×M harder), on the compositor.
    const box = beat.box === 'p' ? boxP.current : beat.box === 'm' ? boxM.current : null;
    if (box && !reduceMotion && !ink && typeof box.animate === 'function') {
      const heavy = step?.op === 'x';
      const timing = { duration: heavy ? 340 : 280, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)' };
      box.animate([{ transform: `scale(${heavy ? 1.2 : 1.12})` }, { transform: 'none' }], timing);
      box
        .querySelector('.tally-flash')
        ?.animate([{ opacity: heavy ? 0.7 : 0.45 }, { opacity: 0 }], timing);
    }
    if (step?.source === 'fullCircle') {
      const pawn = document.querySelector(`[data-pawn="${CSS.escape(pawnId)}"]`);
      if (pawn) {
        const r = pawn.getBoundingClientRect();
        setEnso({
          key: performance.now() + i,
          x: r.left + r.width / 2,
          y: r.top + r.height / 2,
          r: Math.max(r.width, r.height) * 0.62,
        });
        sounds.play('enso');
      }
    }
  });

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const at = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms));
    const p = SCORE_PACE[pace];
    let when = 250;
    plan.forEach((b, i) => {
      at(when, () => {
        setCounted(i + 1);
        fire(i);
        if (b.item) return;
        launch(i);
        setHit(i + 1);
        count(i);
      });
      if (b.item) {
        // It lands over the count with a tap, like a card put down; then its chip goes.
        if (!ink) at(when + b.fly, () => sounds.play('tap'));
        at(when + b.launch, () => launch(i));
        at(when + b.hit, () => {
          setHit(i + 1);
          count(i);
        });
      }
      when += b.d;
    });
    // A hush before the slam (§11.7).
    at(when, () => setPhase('hush'));
    when += reduceMotion ? 60 : 120;
    at(when, () => {
      setPhase('slam');
      if (ink) sounds.play('nib');
      else sounds.play(tier === 2 ? 'slamBig' : 'slam');
      if (flare && !ink) {
        sounds.play('flare');
        flareScene(1500);
      }
    });
    when += p.slam + (flare && !ink ? 600 : 0);
    if (record && !ink) at(when - 200, () => sounds.play('record'));
    at(when, () => {
      setPhase('roll');
      landed();
    });
    when += p.roll + p.linger + (record ? 500 : 0) + (inkOut ? 1400 : 0);
    at(when, () => setPhase('leaving'));
    at(when + 350, done);
    return () => timers.forEach(clearTimeout);
  }, [plan, pace, reduceMotion, ink, tier, flare, record, inkOut]);

  // The smoke hushes with the count's last breath, and the lamp knows a flare.
  useEffect(() => {
    if (phase === 'hush') patchScene({ hush: true });
    else patchScene({ hush: false });
  }, [phase]);
  useEffect(() => () => patchScene({ hush: false }), []);

  // An item flies up from where it sits on the table (its Mantel niche, its pawn, its square) to
  // its place over the count, growing as it comes; from just below when the table doesn't show it.
  const flyNow = plan[counted - 1]?.fly ?? 0;
  useLayoutEffect(() => {
    const el = actorAt.current;
    if (!el || flyNow === 0 || typeof el.animate !== 'function') return;
    const a = el.getBoundingClientRect();
    const r = sourceOf(steps[counted - 1], pawnId)?.getBoundingClientRect();
    const seen = r && r.width > 0 && r.bottom > 0 && r.top < window.innerHeight;
    const dx = seen ? r.left + r.width / 2 - (a.left + a.width / 2) : 0;
    const dy = seen ? r.top + r.height / 2 - (a.top + a.height / 2) : 28;
    const s0 = seen ? Math.min(1, Math.max(0.3, r.width / a.width)) : 0.5;
    el.animate(
      [
        {
          transform: `translate(calc(-50% + ${dx}px), calc(-100% - 6px + ${dy}px)) scale(${s0})`,
          opacity: seen ? 1 : 0,
        },
        { transform: 'translate(-50%, calc(-100% - 6px))', opacity: 1 },
      ],
      { duration: flyNow, easing: 'cubic-bezier(0.25, 0.7, 0.25, 1)' },
    );
  }, [counted, flyNow, steps, pawnId]);

  // The row of items that have counted stays centered: when one joins, the others slide over to
  // make room for it rather than jumping (FLIP, by their positions in the row).
  const castAt = useRef<HTMLSpanElement>(null);
  const castX = useRef(new Map<string, number>());
  useLayoutEffect(() => {
    const row = castAt.current;
    if (!row) return;
    const next = new Map<string, number>();
    row.querySelectorAll<HTMLElement>('[data-cast]').forEach((el) => {
      const key = el.dataset.cast!;
      const x = el.offsetLeft;
      next.set(key, x);
      const was = castX.current.get(key);
      if (was !== undefined && was !== x && !reduceMotion && typeof el.animate === 'function')
        el.animate([{ translate: `${was - x}px 0` }, { translate: '0 0' }], {
          duration: 280,
          easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)',
        });
    });
    castX.current = next;
  });

  const skip = () => {
    setCounted(beats.length);
    setHit(beats.length);
    onLanded();
    onDone();
  };

  // The boxes show what has landed in them, a moment behind an item's beat.
  const inBox = hit > 0 ? beats[hit - 1] : undefined;
  const scored = phase === 'slam' || phase === 'roll' || phase === 'leaving';
  const shownP = scored ? breakdown.points.total : (inBox?.p ?? 0);
  const shownM = scored ? breakdown.multiplier.total : (inBox?.m ?? 0);
  const brass = beats.slice(0, hit).some((b) => b.box === 'brass') ? (breakdown.brass ?? 0) : 0;
  const patternSeen = steps
    .slice(0, counted)
    .find((s) => s.source === 'pattern' || s.source === 'fullCircle');
  const pattern = breakdown.multiplier.pattern;
  const sparks = ink || reduceMotion ? 0 : [6, 9, 12][tier]!;
  // The boxes are as wide as the widest number they will show, from the start.
  const roomP = useMemo(
    () => widest([...beats.map((b) => b.p), breakdown.points.total].map(formatScore)),
    [beats, breakdown.points.total],
  );
  const roomM = useMemo(
    () => widest([...beats.map((b) => b.m), breakdown.multiplier.total].map(formatM)),
    [beats, breakdown.multiplier.total],
  );
  // Rows that come later are kept from the start, hidden until then.
  const castRow = !ink && plan.some((b) => b.item);
  const patternRow = !!(pattern || breakdown.multiplier.fullCircle);
  const brassRow = beats.some((b) => b.box === 'brass') && (breakdown.brass ?? 0) > 0;
  // The item over the count (or a Stamp's or Finish's card): the one firing now, and the one
  // before it on its way out, the same element as when it fired, so it leaves smoothly.
  const shown = (isOne: (i: number) => boolean) => {
    const seen: number[] = [];
    for (let i = 0; i < counted; i++) if (isOne(i)) seen.push(i);
    const now = !scored && seen.at(-1) === counted - 1 ? counted - 1 : undefined;
    // Still, nothing is seen leaving: it is simply gone.
    if (reduceMotion) return { now, gone: undefined };
    return { now, gone: now === undefined ? seen.at(-1) : seen.at(-2) };
  };
  const cardOf = (i: number): Card | undefined => {
    const st = steps[i];
    return st && (st.source === 'stamp' || st.source === 'finish') && st.uid
      ? cards[st.uid]
      : undefined;
  };
  const actors = shown((i) => !!plan[i]?.item);
  const firingCards = shown((i) => cardOf(i) !== undefined);
  // What each beat counted, as a line under the boxes: this one, and the last one leaving.
  const lines = scored ? [] : [reduceMotion ? -1 : counted - 2, counted - 1].filter((i) => i >= 0);
  // Every item that has counted so far, with what it added in all.
  const cast = useMemo(() => {
    const out: { key: string; step: TallyStep; texts: string[] }[] = [];
    steps.slice(0, hit).forEach((st, i) => {
      if (!actorOf(st, 1)) return;
      const key = st.uid ?? `${st.source}:${st.id}`;
      const text = beats[i]?.text ?? '';
      const found = out.find((c) => c.key === key);
      if (found) found.texts.push(text);
      else out.push({ key, step: st, texts: [text] });
    });
    return out;
  }, [steps, beats, hit]);
  const style = { ...(tint ? { '--tally-tint': tint } : {}) } as CSSProperties;

  const lineOf = (i: number, leaving: boolean) => {
    const b = beats[i];
    const st = steps[i];
    if (!b) return null;
    return (
      <span
        key={`line-${i}`}
        ref={leaving ? undefined : beatAt}
        className={`tally-beat beat-${b.box} ${st?.op === 'x' ? 'is-x' : ''} ${
          b.lucky ? 'is-lucky' : ''
        } ${leaving ? 'is-leaving' : ''}`}
        aria-hidden="true"
      >
        <b>{b.text}</b> {actorName(st) ?? b.label}
        {b.lucky ? ` · ${t('score.lucky')}` : ''}
      </span>
    );
  };
  const actorAtBeat = (i: number | undefined, leaving: boolean) => {
    if (i === undefined) return null;
    return (
      <span
        key={`actor-${i}`}
        ref={leaving ? undefined : actorAt}
        className={`tally-actor beat-${beats[i]?.box ?? 'p'} ${steps[i]?.op === 'x' ? 'is-x' : ''} ${
          leaving ? 'is-leaving' : ''
        }`}
        style={leaving ? undefined : ({ '--fly': `${flyNow}ms` } as CSSProperties)}
        aria-hidden="true"
      >
        <span className="tally-actor-body">{actorOf(steps[i], 56)}</span>
      </span>
    );
  };
  const cardAtBeat = (i: number | undefined, leaving: boolean) => {
    const card = i === undefined ? undefined : cardOf(i);
    if (i === undefined || !card) return null;
    return (
      <span
        key={`card-${i}`}
        className={`tally-card ${leaving ? 'is-leaving' : ''}`}
        aria-hidden="true"
      >
        <CardView
          size="sm"
          face={card.kind === 'normal' ? card.face : null}
          wild={card.kind === 'wild'}
          stamp={card.stamp}
          finish={card.finish}
          label=""
        />
      </span>
    );
  };

  return (
    <div
      className={`tally half-${half} phase-${phase} tier-${tier} ${ink ? 'is-ink' : ''} ${
        flare && scored && !ink ? 'is-flaring' : ''
      } ${reduceMotion ? 'is-still' : ''}`}
      style={style}
      role="status"
      title={t('score.skip')}
      onClick={skip}
    >
      {scored && sparks > 0 && (
        <span className="burst-sparkles" aria-hidden="true">
          {SPARKS.slice(0, sparks).map(([left, top, delay], i) => (
            <span
              key={i}
              className="fx"
              style={{ left: `${left}%`, top: `${top}%`, animationDelay: `${delay}ms` }}
            />
          ))}
        </span>
      )}
      {/* Who scores, and Skip beside it: kept in its place once the score is in, so nothing moves. */}
      <span className="count-head">
        <span className="tally-who">
          {who} · {source === 'epitaph' ? t('score.epitaph') : t('score.chain')}
        </span>
        <button
          type="button"
          className={`count-skip ${scored ? 'is-done' : ''}`}
          aria-hidden={scored || undefined}
          tabIndex={scored ? -1 : undefined}
          aria-label={t('score.skip')}
          onClick={(e) => {
            e.stopPropagation();
            skip();
          }}
        >
          {t('score.skipButton')}
          <svg
            viewBox="0 0 8 7"
            width="10"
            height="9"
            aria-hidden="true"
            shapeRendering="crispEdges"
          >
            <path
              d="M0 0h1v1h1v1h1v1h1v1h-1v1h-1v1h-1v1h-1zM4 0h1v1h1v1h1v1h1v1h-1v1h-1v1h-1v1h-1z"
              fill="currentColor"
            />
          </svg>
        </button>
      </span>
      <span className="tally-formula">
        <span ref={boxP} className="tally-box tally-p">
          <span className="tally-flash" aria-hidden="true" />
          <span key={`p${shownP}`} className="tally-num">
            <ScoreNumber value={shownP} />
          </span>
          <span className="tally-room" aria-hidden="true">
            {roomP}
          </span>
        </span>
        <span className="tally-x">×</span>
        <span ref={boxM} className="tally-box tally-m">
          <span className="tally-flash" aria-hidden="true" />
          <span key={`m${shownM}`} className="tally-num">
            {formatM(shownM)}
          </span>
          <span className="tally-room" aria-hidden="true">
            {roomM}
          </span>
        </span>
      </span>
      {cardAtBeat(firingCards.gone, true)}
      {cardAtBeat(firingCards.now, false)}
      {actorAtBeat(actors.gone, true)}
      {actorAtBeat(actors.now, false)}
      {/* One place under the boxes: each beat's line while it counts, then the score. */}
      <span className="tally-stage">
        {lines.map((i) => lineOf(i, i < counted - 1))}
        {scored && (
          <span
            className={`tally-score ${inkOut ? 'is-ink-out' : ''} ${flare && !ink ? 'is-flare' : ''}`}
          >
            <span className="sr-only">= </span>
            <span className="tally-score-num">
              <ScoreNumber value={score} />
            </span>
            {ink && <span className="tally-underline" aria-hidden="true" />}
            {inkOut && (
              <>
                <span className="tally-drips" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                <span className="tally-ink-out">{t('tally.inkOut')}</span>
              </>
            )}
          </span>
        )}
      </span>
      {patternRow && (
        <span className={`tally-pattern ${patternSeen || scored ? 'is-seen' : ''}`}>
          {pattern && (
            <span>
              {patternName(pattern)}
              {level > 1 && <b className="tally-level">{roman(level)}</b>}
            </span>
          )}
          {breakdown.multiplier.fullCircle && <span>{t('preview.fullCircle')}</span>}
        </span>
      )}
      {castRow && (
        <span ref={castAt} className="tally-cast" aria-hidden="true">
          {cast.slice(0, CAST_MAX).map((c) => (
            <span key={c.key} data-cast={c.key} className="tally-cast-item">
              {actorOf(c.step, 22)}
              <i>{c.texts.join(' ')}</i>
            </span>
          ))}
          {cast.length > CAST_MAX && (
            <span key="more" data-cast="more" className="tally-cast-more">
              +{cast.length - CAST_MAX}
            </span>
          )}
        </span>
      )}
      {record && !ink && (
        <span className={`tally-record ${scored ? 'is-seen' : ''}`}>{t('tally.record')}</span>
      )}
      {brassRow && (
        <span className={`burst-brass ${brass > 0 ? 'is-seen' : ''}`}>
          +¤{breakdown.brass ?? 0}
        </span>
      )}
      {createPortal(
        <div className="tally-layer" aria-hidden="true">
          {chips.map((c) => (
            <span
              key={c.key}
              className={`tally-chip chip-${c.box} ${c.heavy ? 'is-heavy' : ''}`}
              style={
                {
                  left: c.x,
                  top: c.y,
                  '--dx': `${c.dx}px`,
                  '--dy': `${c.dy}px`,
                } as CSSProperties
              }
              onAnimationEnd={() => setChips((all) => all.filter((x) => x.key !== c.key))}
            >
              {c.text}
            </span>
          ))}
          {enso && (
            <svg
              key={enso.key}
              className="tally-enso"
              style={{ left: enso.x - enso.r, top: enso.y - enso.r }}
              width={enso.r * 2}
              height={enso.r * 2}
              viewBox="0 0 100 100"
              onAnimationEnd={() => setEnso(null)}
            >
              {/* One brushstroke, its gap at the upper right. */}
              <path
                d="M78 18 A40 40 0 1 0 90 44"
                fill="none"
                stroke="var(--gold)"
                strokeWidth="7"
                strokeLinecap="round"
                pathLength={100}
              />
            </svg>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

/** Items shown in the row under the count; the rest are counted as "+n". */
const CAST_MAX = 4;

/** The longest of some texts (a box's widest number). */
const widest = (texts: readonly string[]): string =>
  texts.reduce((a, b) => (b.length > a.length ? b : a), '');

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
const roman = (n: number): string => ROMAN[n - 1] ?? String(n);
